import { ISectionLogger } from "@multiweb/logging";
import { AppContext } from "App/AppContext";
import { StandardOperationRights } from "Global/Base/Rights/StandardOperationRights";
import { ErrorHandler } from "UI/Errors";
import { ListDataGrid } from "UI/UI.Controls/ListDataGrid";
import { DataConverter } from "../../../../../Global/Base/Converters/DataConverter";
import { DIContainer } from "../../../../../Global/Base/DIContainer";
import { Event as GlobalEvent, StatefulEvent } from "../../../../../Global/Base/Events";
import { CancellationToken } from "../../../../../Global/Base/Promises/CancellationTokenSource";
import { Deferred, PromiseUtils } from "../../../../../Global/Base/Promises/PromiseUtils";
import { IRightsManager } from "../../../../../Global/Base/Rights/IRightsManager";
import { EMPTY_ITEM_RIGHTS_COLLECTION, RightType } from "../../../../../Global/Base/Rights/RightTypes";
import { ConditionOperator, StringUtils } from "../../../../../Global/Base/Utils/StringUtils";
import { OWObject } from "../../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { OWObjectList } from "../../../../../OW.Remoting/Remoting/DataTypes/OWObjectList";
import { IWorkflowMenuItem } from "../../../../../OW.Remoting/Remoting/IWorkflowManager";
import { Localization } from "../../../../../OW.Remoting/Remoting/Localization";
import { OWManager } from "../../../../../OW.Remoting/Remoting/OWManager";
import { RequestContext } from "../../../../../OW.Remoting/Remoting/RequestContext";
import { IAwaitableEvent } from "../../../../UI.BaseControls/AwaitableEvent";
import { EventHelper } from "../../../../UI.BaseControls/Helpers/EventHelper";
import { ActionType, IAction } from "../../../../UI.Controls/Actions/IAction";
import { MenuType } from "../../../../UI.Controls/Actions/MenuType";
import { TwoListEmptyDataGridMessage } from "../../../../UI.Controls/DataGridSubcontrols/DataGridMessages/TwoListEmptyDataGridMessage";
import { createElement } from "../../../../UI.ElementarControls/Utils/CustomElementUtils";
import { ComponentTree } from "../../../LogicalTree/ComponentTree";
import { ComponentTreeType } from "../../../LogicalTree/Definitions";
import { RightsEvaluationOverridesType, RightsManagerType } from "../../../Rights/Definitions";
import { ControlRightTypesGroup, RightGroups } from "../../../Rights/RightGroups";
import { RightsEvaluationOverrides } from "../../../Rights/RightsEvaluationOverrides";
import { convertContextFromFrameToRequest } from "../../../Utilities/SharedUIMethods";
import { FormControlParams, FormControlWithLoggerParams } from "../../Controls/ControlInterfaces";
import { LocalizedStringBuilder } from "../../DataBuilders/LocalizedStringBuilder";
import { IMenu } from "../../FormUI/FormUI";
import { MessageButtonType, MessageWindow } from "../../MessageWindow";
import { UIManager } from "../../UIManager";
import { DetailFrame } from "../DetailFrame";
import { FrameSelChanged, HideButtons, IActualObjectChangedEventArgs } from "../Frames";
import { IForm } from "../IForm";
import { IFrame, IFrameCtxMenuData } from "../IFrame";
import { IMasterableFrame } from "./FrameInterfaces";
import { FrameSharedMethods, IExtendedOperationsParams } from "./FrameSharedMethods";
import { IEditableFrame } from "./IEditableFrame";
import { ITwoListFrame } from "./ITwoListFrame";
import { ListFrame } from "./ListFrame";
import { MasterableFormControl } from "./MasterableFormControl";
import { TwoListFrameRightsEvaluationOverrides } from "./TwoListFrameRightsEvaluationOverrides";

export class TwoListFrame extends MasterableFormControl implements ITwoListFrame {
    private readonly _logger: ISectionLogger;

    public get canDelete(): boolean | undefined | null {
        return this._rightListFrame.canDelete;
    }

    public get isTwoListFrame(): boolean {
        return true;
    }
    public get isEditing(): boolean {
        return this._rightListFrame.isEditing;
    }
    public set isEditing(value: boolean) {
        this._rightListFrame.isEditing = value;
    }

    public get isEditable(): boolean {
        return this._rightListFrame.isEditable;
    }
    public get detail(): DetailFrame {
        return this._rightListFrame.detail;
    }
    public set detail(value: DetailFrame) {
        this._rightListFrame.detail = value;
    }
    public get detailForm(): string {
        return this._rightListFrame.detailForm;
    }
    public set detalForm(value: string) {
        this._rightListFrame.detailForm = value;
    }
    public get changesCanceled(): GlobalEvent<IMasterableFrame, void> {
        return this._rightListFrame.changesCanceled;
    }
    public get recordChangesCanceled(): GlobalEvent<IMasterableFrame, string> {
        return this._rightListFrame.recordChangesCanceled;
    }
    public get editingRow(): GlobalEvent<IFrame, OWObject> {
        return this._rightListFrame.editingRow;
    }
    public get actualObjectSaved(): GlobalEvent<IMasterableFrame, OWObject> {
        return this._rightListFrame.actualObjectSaved;
    }
    public get dataLoaded(): GlobalEvent<IMasterableFrame, any> {
        return this._rightListFrame.dataLoaded;
    }
    public get dataLoadingCancelled(): GlobalEvent<IMasterableFrame, any> {
        return this._rightListFrame.dataLoadingCancelled;
    }
    public get dataLoadingFailed(): GlobalEvent<IMasterableFrame, any> {
        return this._rightListFrame.dataLoadingFailed;
    }
    public get editModeChanged(): GlobalEvent<IEditableFrame, boolean> {
        return this._rightListFrame.editModeChanged;
    }
    public get detailComponentTree(): ComponentTreeType {
        return this.detail.componentTree;
    }

    public async waitForSelectedRecordChange(): Promise<boolean> {
        return this._rightListFrame.waitForSelectedRecordChange();
    }

    //#region Fields
    public readonly canBeMasterFrame: boolean = true;
    public readonly frameName: string;
    public readonly masterFrameName: string;

    public refreshObjectAfterSetActive: boolean = false;
    public refreshListAfterSetActive: boolean = false;

    protected _formCanRead: boolean | undefined = undefined;
    protected _interactive: boolean = true;
    private _leftListFrame: LeftListFrame;
    private _rightListFrame: RightListFrame;
    private _actions: IAction[] = [];
    private _leftCaptionElement: HTMLElement;
    private _rightCaptionElement: HTMLElement;
    private _leftLocKey: string;
    private _rightLocKey: string;
    private _leftCondition: string;
    private _framesLinkAttr: string; // Vazba levý - pravý
    private _afterGuiCreateFill: string;
    private _afterGuiCreateMethod: string;
    private _listAutomaticCondition: boolean;
    private _permanentInvisible: boolean = false;
    private _hideButtons: HideButtons;

    /**
     * Zakáže možnost řazení
     */
    private readonly _disableSort: boolean = false;

    protected _isInitialized: boolean = false;
    protected _initializationStarted: boolean = false;

    /**
     * Chain promisů s probihajicimi updaty dat
     */
    private _waitForUpdatesTask: Promise<void> = Promise.resolve();
    /**
     * Promise s probíhajícím vyhodnocováním práv
     */
    private _rightsEvaluationTask: Promise<void> = Promise.resolve();
    private _selectionChanged: GlobalEvent<IMasterableFrame, FrameSelChanged> = new GlobalEvent<IMasterableFrame, FrameSelChanged>();
    private _actualObjectChanged: GlobalEvent<IMasterableFrame, IActualObjectChangedEventArgs> = new GlobalEvent<IMasterableFrame, IActualObjectChangedEventArgs>();
    private _actualObjectValueChanged: GlobalEvent<IFrame, OWObject> = new GlobalEvent<IFrame, OWObject>();

    protected _componentTree: ComponentTreeType;
    protected _rightsManager: RightsManagerType = DIContainer.instance.create<RightsManagerType>(IRightsManager, this, this._createOrGetRightsOverrides());
    protected __rightsEvaluationOverrides: RightsEvaluationOverridesType;
    protected _actualObjectStandardRights: StandardOperationRights;

    //#endregion Fields

    //#region Events
    public readonly contextActionsReady: GlobalEvent<IMasterableFrame, string> = new GlobalEvent<IMasterableFrame, string>();
    public readonly initialized: StatefulEvent<IFrame, void> = new StatefulEvent<IFrame, void>();
    public readonly frameIsVisible: GlobalEvent<IFrame, any> = new GlobalEvent<IFrame, any>();
    public readonly contextMenuOpening: GlobalEvent<IFrame, IFrameCtxMenuData> = new GlobalEvent<IFrame, IFrameCtxMenuData>();
    public readonly selectionModeChanged: GlobalEvent<IMasterableFrame, boolean> = new GlobalEvent<IMasterableFrame, boolean>();
    //#endregion Events

    //#region Properties
    public readonly uuid: string;

    public get componentTree(): ComponentTreeType {
        return this._componentTree;
    }

    public set componentTree(componentTree: ComponentTreeType) {
        this._componentTree = componentTree;
    }

    public get rightsManager(): RightsManagerType {
        return this._rightsManager;
    }

    public get isMainFrame(): boolean {
        return this._rightListFrame.isMainFrame;
    }

    public set isMainFrame(value: boolean) {
        this._rightListFrame.isMainFrame = value;
    }

    public get formCanRead(): boolean {
        return this._formCanRead;
    }

    public set formCanRead(val: boolean) {
        this._formCanRead = val;
        this._rightListFrame.formCanRead = val;
        this._leftListFrame.formCanRead = val;
    }

    public get initializationStarted(): boolean {
        return this._initializationStarted;
    }

    public get isInitialized(): boolean {
        return this._isInitialized;
    }

    public get renderStandardRowActions(): boolean {
        return true;
    }

    public override get masterFrame(): IMasterableFrame {
        return this._rightListFrame.masterFrame;
    }

    public override set masterFrame(frame: IMasterableFrame) {
        this._leftListFrame.masterFrame = frame;
        this._rightListFrame.masterFrame = frame;
    }

    public get masterObject(): OWObject {
        return this._rightListFrame.masterObject;
    }

    public set masterObject(object: OWObject) {
        this._rightListFrame.masterObject = object;
    }

    public get masterLinkToAttr(): string | null {
        return this._rightListFrame.masterLinkToAttr;
    }

    public set masterLinkToAttr(value: string | null) {
        this._rightListFrame.masterLinkToAttr = value;
    }

    public get masterAttr(): string | null {
        return this._rightListFrame.masterAttr;
    }

    public set masterAttr(value: string | null) {
        this._rightListFrame.masterAttr = value;
    }

    public get canInsert(): boolean | undefined | null {
        return this._rightListFrame.canInsert;
    }

    public set canInsert(value: boolean) {
        this._leftListFrame.canInsert = value;
    }

    public override get visible(): boolean {
        return super.visible;
    }

    public override set visible(value: boolean) {
        super.visible = value;

        if (value && !this._permanentInvisible) {
            this.element.style.removeProperty("display");
        } else {
            this.element.style.display = "none";
        }

        this._leftListFrame.visible = value;
        this._rightListFrame.visible = value;
    }

    public get interactive(): boolean {
        return this._interactive;
    }

    public set interactive(value: boolean) {
        this._interactive = value;
    }

    public override get enabled(): boolean {
        return super.enabled;
    }

    public override set enabled(value: boolean) {
        super.enabled = value;

        this._leftListFrame.enabled = value;
        this._rightListFrame.enabled = value;
    }

    public get isActive(): boolean {
        return this.enabled && this.interactive;
    }

    public get hideToolBar(): boolean {
        return false;
    }

    public get actualObject(): OWObject {
        return this._rightListFrame.actualObject;
    }

    public set actualObject(_object: OWObject) {
        // do nothing.
    }

    public get actualObjectStandardRights(): StandardOperationRights {
        return this._rightListFrame.actualObjectStandardRights;
    }

    public get menu(): IMenu {
        return this._rightListFrame.menu;
    }

    public set leftCondition(value: string) {
        this._leftListFrame.condition = value;
    }

    public set leftConditionParams(params: readonly string[]) {
        this._leftListFrame.conditionParamsAttrs = params;
    }

    public get rightCondition(): string {
        return this._rightListFrame.condition;
    }

    public set rightCondition(value: string) {
        this._rightListFrame.condition = value;
    }

    public get rightConditionParams(): readonly string[] {
        return this._rightListFrame.conditionParamsAttrs;
    }

    public set rightConditionParams(params: readonly string[]) {
        this._rightListFrame.conditionParamsAttrs = params;
    }

    public get hideButtons(): HideButtons {
        return this._hideButtons;
    }

    public set hideButtons(value: HideButtons) {
        this._hideButtons = value;
        const newValue = { ...value, insert: true, delete: true, copy: true };
        this._leftListFrame.hideButtons = newValue;
        this._rightListFrame.hideButtons = newValue;
    }

    public get hasSelectedRecord(): boolean {
        return this._leftListFrame.hasSelectedRecord;
    }

    public get actualObjectChanged(): GlobalEvent<IMasterableFrame, IActualObjectChangedEventArgs> {
        return this._actualObjectChanged;
    }

    public get actualObjectValueChanged(): GlobalEvent<IFrame, OWObject> {
        return this._actualObjectValueChanged;
    }

    public get selectionChanged(): GlobalEvent<IMasterableFrame, FrameSelChanged> {
        return this._selectionChanged;
    }

    public get workflowItemsChanging(): GlobalEvent<IFrame, IWorkflowMenuItem[]> {
        return this._rightListFrame.workflowItemsChanging;
    }

    public get suppressSaveDialog(): boolean {
        return false;
    }

    /**
     * Vrací promis s probíhajícím vyhodnocováním práv, na které lze čekat
     */
    public get rightsEvaluationTask(): Promise<void> {
        return Promise.all([this._leftListFrame.rightsEvaluationTask, this._rightListFrame.rightsEvaluationTask, this._rightsEvaluationTask]).then(() => {
            // do nothing.
        });
    }

    public get childFrames(): IFrame[] {
        return [this._leftListFrame, this._rightListFrame];
    }
    //#endregion Properties

    //#region Constructors
    constructor(params: FormControlWithLoggerParams) {
        super(params);
        const { ownerForm, element, logger } = params;

        this._logger = logger;
        this.element.classList.add("two-list-frame");

        this.frameName = element.getAttribute("data-frameName");
        this.masterFrameName = this.element.getAttribute("data-masterFrame");
        this.objectName = this.element.getAttribute("data-objectName");
        this._leftCondition = this.element.getAttribute("data-listCondition"); // XG transformuje TListCondition na ListCondition
        this._framesLinkAttr = this.element.getAttribute("data-attrName");
        this._listAutomaticCondition = DataConverter.toBoolean(this.element.getAttribute("data-listAutomaticCondition"), true);
        this._afterGuiCreateFill = this.element.getAttribute("data-afterGuiCreateFill");
        this._afterGuiCreateMethod = this.element.getAttribute("data-afterGuiCreateMethod");
        this._permanentInvisible = !DataConverter.toBoolean(this.element.getAttribute("data-visible"), true);
        this._leftLocKey = this.element.getAttribute("data-TListLocKey");
        this._rightLocKey = this.element.getAttribute("data-captionlocKey");
        this._disableSort = DataConverter.toBoolean(this.element.getAttribute("data-DisableSort"), false);

        const partialFrameParams: PartialFrameParams = {
            isEditable: DataConverter.toBoolean(this.element.getAttribute("data-isEditable"), false),
            reportAttribute: this.element.getAttribute("data-printMenu"),
            exportAttribute: this.element.getAttribute("data-exportable"),
            defaultGroup: this.element.getAttribute("data-defaultGroup"),
            groupWidth: this.element.getAttribute("data-groupWidth"),
            showOnMainForm: this.getDataAttr("showOnMainForm"),
            showFilterValues: this.getDataAttr("showFilterValues"),
            ownerForm,
        };

        const [ rightOuterElement, rightFrameElement, rightAttributes ] = this._createRightListFrame(partialFrameParams);
        const [ leftOuterElement, leftFrameElement, leftAttributes ] = this._createLeftListFrame(partialFrameParams);

        // --------------------Vložení prvků do stránky ----------------------------------

        // TODO: Nebylo by lepší ten default XG-666 prostě změnit v XG tak, aby negeneroval nic?
        if ((this._leftLocKey && !this._leftLocKey.endsWith("-666")) || (this._rightLocKey && !this._rightLocKey.endsWith("-666"))) { // Elementy caption chceme vložit do stránky jen pokud je co zobrazit
            this._leftCaptionElement = createElement({ type: "span", classes: "frame-caption" });
            leftOuterElement.appendChild(this._leftCaptionElement);

            this._rightCaptionElement = createElement({ type: "span", classes: "frame-caption" });
            rightOuterElement.appendChild(this._rightCaptionElement);
        }

        leftOuterElement.appendChild(leftFrameElement);
        this.element.appendChild(leftOuterElement);

        rightOuterElement.appendChild(rightFrameElement);
        this.element.appendChild(rightOuterElement);

        //----------------------- Nastavení properties -------------------------------

        this._rightListFrame.detailForm = this.element.getAttribute("data-detailform");
        this.masterLinkToAttr = this.element.getAttribute("data-masterLinkToAttr");
        this.leftCondition = this._leftCondition;
        this.hideButtons = FrameSharedMethods.parseHideButtons(this.ownerForm, this.element.getAttribute("data-hidebuttons"), false);
        this.rightCondition = this.element.getAttribute("data-condition");

        const leftConditionParams = this.element.getAttribute("data-TListCondPar");
        const conditionParams = this.element.getAttribute("data-conditionParamsString");
        const leftOrderBy = this.element.getAttribute("data-TListOrderBy");
        const rightOrderBy = this.element.getAttribute("data-orderBy");

        if (leftConditionParams) {
            this.leftConditionParams = leftConditionParams.split(";");
        }

        if (conditionParams) {
            this.rightConditionParams = conditionParams.split(";");
        }

        if (leftOrderBy) {
            this._leftListFrame.orderBy = FrameSharedMethods.parseOrderBy(leftOrderBy, ListDataGrid.getDefaultOrderByDefinition(this._leftListFrame.dataSource));
        }

        if (rightOrderBy) {
            this._rightListFrame.orderBy = FrameSharedMethods.parseOrderBy(rightOrderBy, ListDataGrid.getDefaultOrderByDefinition(this._rightListFrame.dataSource));
        }

        if (partialFrameParams.isEditable) {
            this._parseListAttributes(this._leftListFrame, leftAttributes);
            this._parseListAttributes(this._rightListFrame, rightAttributes);
        }

        this.uuid = FrameSharedMethods.generateUuid(!!AppContext.instance.configuration.developmentMode, this.frameName);
    }

    private _createLeftListFrame(params: PartialFrameParams): [HTMLElement, HTMLElement, NodeListOf<Element>] {
        const leftOuterElement = createElement({ classes: "left-frame-panel" });
        const leftFrameElement = createElement();

        leftFrameElement.setAttribute("data-tsccontrol", ListFrame.name);
        leftFrameElement.setAttribute("data-frameName", `${this.frameName}_Left`); // `
        leftFrameElement.setAttribute("id", `${this.element.getAttribute("id")}-left`); // ` nastaví id postfix "-left", aby byly id unikátní, ale zůstala možnost výběru
        leftFrameElement.setAttribute("data-objectName", this.element.getAttribute("data-listObjectName"));

        if (params.defaultGroup) {
            leftFrameElement.setAttribute("data-defaultGroup", params.defaultGroup);
        }

        if (params.groupWidth) {
            leftFrameElement.setAttribute("data-groupWidth", params.groupWidth);
        }

        if (params.reportAttribute) {
            leftFrameElement.setAttribute("data-printMenu", params.reportAttribute);
        }

        if (params.exportAttribute) {
            leftFrameElement.setAttribute("data-exportable", params.exportAttribute);
        }

        if (params.showOnMainForm) {
            leftFrameElement.setAttribute("data-showOnMainForm", params.showOnMainForm);
        }

        if (params.showFilterValues) {
            leftFrameElement.setAttribute("data-showFilterValues", params.showFilterValues);
        }

        this._copyElementEmptyAttributes(leftFrameElement, "left");

        const listAttributres = this.element.querySelector("span[data-tscitem^=\"ListAttributes\"]");
        let leftAttributes: NodeListOf<Element>;

        if (listAttributres) {
            leftFrameElement.appendChild(listAttributres);

            if (params.isEditable) {
                leftAttributes = listAttributres.querySelectorAll("span[data-tscitem^=\"ListAttribute\"]");
            }
        }

        this._leftListFrame = new LeftListFrame({
            ownerForm: params.ownerForm,
            element: leftFrameElement,
            originalRightsDefinition: EMPTY_ITEM_RIGHTS_COLLECTION,
        }, this._rightListFrame);
        this._leftListFrame.addCanEditPredicate(() => {
            return true;
        });
        this._leftListFrame.checkAndSaveActualFrameOnly = true;
        this._leftListFrame.disableSort = this._disableSort;

        return [leftOuterElement, leftFrameElement, leftAttributes];
    }

    private _createRightListFrame(params: PartialFrameParams): [rightOuterElement: HTMLElement, rightFrameElement: HTMLElement, rightAttributes: NodeListOf<Element>] {
        const rightOuterElement = createElement({ classes: "right-frame-panel" });
        const rightFrameElement = createElement();

        rightFrameElement.setAttribute("data-tsccontrol", ListFrame.name);
        rightFrameElement.setAttribute("data-frameName", `${this.frameName}_Right`); // `
        rightFrameElement.setAttribute("id", `${this.element.getAttribute("id")}-right`); // ` nastaví id postfix "-right", aby byly id unikátní, ale zůstala možnost výběru
        rightFrameElement.setAttribute("data-objectName", this.objectName);

        if (params.defaultGroup) {
            rightFrameElement.setAttribute("data-defaultGroup", params.defaultGroup);
        }

        if (params.groupWidth) {
            rightFrameElement.setAttribute("data-groupWidth", params.groupWidth);
        }

        if (params.reportAttribute) {
            rightFrameElement.setAttribute("data-printMenu", params.reportAttribute);
        }

        if (params.exportAttribute) {
            rightFrameElement.setAttribute("data-exportable", params.exportAttribute);
        }

        if (params.showOnMainForm) {
            rightFrameElement.setAttribute("data-showOnMainForm", params.showOnMainForm);
        }

        if (params.showFilterValues) {
            rightFrameElement.setAttribute("data-showFilterValues", params.showFilterValues);
        }

        this._copyElementEmptyAttributes(rightFrameElement, "right");

        const attributes = this.element.querySelector("span[data-tscitem^=\"Attributes\"]");

        let rightAttributes: NodeListOf<Element>;

        if (attributes) {
            rightFrameElement.appendChild(attributes);

            if (params.isEditable) {
                rightAttributes = attributes.querySelectorAll("span[data-tscitem^=\"Attribute\"]");
            }
        }

        this._rightListFrame = new RightListFrame({
            ownerForm: params.ownerForm,
            element: rightFrameElement,
            originalRightsDefinition: EMPTY_ITEM_RIGHTS_COLLECTION,
        });
        this._rightListFrame.addCanEditPredicate(() => {
            return true;
        });
        this._rightListFrame.checkAndSaveActualFrameOnly = true;
        this._rightListFrame.disableSort = this._disableSort;

        return [rightOuterElement, rightFrameElement, rightAttributes];
    }
    //#endregion Constructors

    private _copyElementEmptyAttributes(targetElement: HTMLElement, prefix: string): void {
        [
            "emptymessageicon",
            "emptymessagelc",
            "emptyfiltermessageicon",
            "emptyfiltermessagelc",
            "emptyfulltextmessageicon",
            "emptyfulltextmessagelc",
        ].forEach(attribute => {
            this._copyElementAttribute(targetElement, `data-${prefix}${attribute}`, `data-${attribute}`);
        });
    }

    private _copyElementAttribute(targetElement: HTMLElement, sourceAttrName: string, targetAttrName?: string): void {
        if (this.element.hasAttribute(sourceAttrName)) {
            targetElement.setAttribute(targetAttrName || sourceAttrName, this.element.getAttribute(sourceAttrName));
        }
    }

    //#region Methods
    /**
     * Ověří, zda na atributech seznamu existuje nějaký editovatelný a podle toho mu povolí editovatelnost
     * @param attributes - pole atributů k prověření
     * @param listFrame - seznam, u kterého má být prověřena editovatelnost atributů
     */
    private _parseListAttributes(listFrame: ListFrame, attributes: NodeListOf<Element>): void {
        for (const attribute of attributes) {
            if (DataConverter.toBoolean(attribute.getAttribute("data-isEditable"), false)) {
                listFrame.isEditable = true;
                listFrame.element.classList.add("editable");
            }
        }
    }

    /**
     * Do fronty s vyhodnocováním práv přidá další ukol, který se provede, až doběhnou úkoly předchozí
     * @param task Vrací promis, na který lze čekat
     */
    public async enqueueRightsEvaluationTask(task: Promise<void>): Promise<void> {
        this._leftListFrame.enqueueRightsEvaluationTask(task);
        this._rightListFrame.enqueueRightsEvaluationTask(task);

        return this._rightsEvaluationTask = this._rightsEvaluationTask.then(() => task);
    }

    /**
     * Vrací, zda je předaný frame přímou subkomponentou TLF
     */
    public isSubframe(frame: IMasterableFrame): boolean {
        return this._rightListFrame === frame;
    }

    /**
     * Znovunacteni dat do obou seznamů.
     * @param manualRefresh Jestli je akce vyvolana uzivatelem (zabranuje nebo povoleuju oznaceni radku v seznamu)
     * @param dontReselect Zda má být objekt znovu vybrán
     */
    public async refreshData(manualRefresh: boolean, dontReselect: boolean = false): Promise<any> {
        const prevObj = this.actualObject;

        return Promise.all([
            this._leftListFrame.refreshData(manualRefresh),
            this._rightListFrame.refreshData(manualRefresh),
            this._leftListFrame.refreshGroupTree(true),
            this._rightListFrame.refreshGroupTree(true),
        ],
        ).then(() => {
            if (!dontReselect) {
                this._rightListFrame.clearActualObject();
                if (prevObj && prevObj.id)
                    this._rightListFrame.selectRow(prevObj.id, true);
            }

            FrameSharedMethods.refreshFrameAfterSetActive(this.ownerForm);
        });
    }

    public async refreshActualObject(): Promise<void> {
        await this._rightListFrame.refreshActualObject();
    }

    public async waitForDataLoad(): Promise<boolean> {
        const results = await Promise.all([this._leftListFrame.waitForDataLoad(), this._rightListFrame.waitForDataLoad()]);
        return results.every(result => result);
    }

    public async waitForPendingUpdates(): Promise<void> {
        await Promise.all([this._leftListFrame.waitForPendingUpdates(), this._rightListFrame.waitForPendingUpdates(), this._waitForUpdatesTask]);
    }

    /**
     * Počká na dokončení probíhajícího nastavení ActualObjectu na všech nadřízených framech a potom na tomto
     */
    public async waitForActualObjectSet(): Promise<boolean> {
        if (this.masterFrame) {
            await this.masterFrame.waitForActualObjectSet();
        }

        return await this._rightListFrame.waitForActualObjectSet();
    }

    /**
     * Došlo ke změně objektu např. na detailu
     * @param actualObject - změněný objekt
     */
    public triggerActualObjectValueChange(actualObject: OWObject): void {
        this._rightListFrame.triggerActualObjectValueChange(actualObject);
    }

    /**
     * Zaregistruje probihajici zmenu dat
     */
    public async enqueuePendingUpdateTask(task: Promise<void>): Promise<void> {
        // je nutne predat na listFrame, ktery vyvolava ulozeni
        this._rightListFrame.enqueuePendingUpdateTask(task);
        return this._waitForUpdatesTask = this._waitForUpdatesTask.then(() => task);
    }

    /**
     * Vyhodnocuje práva formuláře (editace, vložení, mazání)
     * @param ct
     */
    public async actualizeRights(): Promise<void> {
        await this._rightsManager.evaluateRights(RightGroups.getControlRightGroupTypes(ControlRightTypesGroup.IED), null, this.isMainFrame); //TODO-rites: ?? chceme to tu, když to v podstatě spouští vyhodnocení nad nadřízeným framem? nebude stačit že si to nadřízený vyřídí sám? A tento DF by potom přebíral výsledek?
    }

    protected async _initializeRights(): Promise<void> {
        this.componentTree = null;

        this._actualObjectStandardRights = FrameSharedMethods.getDefaultIEDRights(this.ownerForm, this.ownerForm.formContext?.roles);

        this._buildComponentTree();

        await this.actualizeRights();

        if (this.masterFrame == null) {
            this.ownerForm.framesInitialized.add(async () => {
                await this._newEvaluateRightsGroup(ControlRightTypesGroup.IED | ControlRightTypesGroup.initialized); // TODO-rites: ~nechat tady? asi jo; c2: spouštět až po inicializaci všech framů, jinak mohlo dojít k situaci, že se na LF iniciuje vyhodnocení práv a stromem projde na podřízený detail, který ještě není inicializován a chybí mu třeba rightsManager
            });
        } else {
            this._newEvaluateRightsGroup(ControlRightTypesGroup.initialized);
        }
    }
    protected _buildComponentTree(): void {
        this.__rightsEvaluationOverrides = this._createOrGetRightsOverrides();

        this._componentTree = ComponentTree.buildComponentTree(this, {
            frameContext: this.frameContext,
            rightsEvaluationOverrides: this.__rightsEvaluationOverrides,
            isPreinitialized: !!this.componentTree,
            stopTreeBuildOnNextFrame: true,
        });

        const leftComponentTree = ComponentTree.buildComponentTree(this._leftListFrame, {
            frameContext: this.frameContext,
            rightsEvaluationOverrides: new TwoListFrameRightsEvaluationOverrides(this._leftListFrame),
            isPreinitialized: !!this._leftListFrame.componentTree,
            stopTreeBuildOnNextFrame: true,
        });
        const rightComponentTree = ComponentTree.buildComponentTree(this._rightListFrame, {
            frameContext: this.frameContext,
            rightsEvaluationOverrides: new TwoListFrameRightsEvaluationOverrides(this._rightListFrame),
            isPreinitialized: !!this._rightListFrame.componentTree,
            stopTreeBuildOnNextFrame: true,
        });

        this._componentTree.addNode(leftComponentTree);
        this._componentTree.addNode(rightComponentTree);
    }

    protected _createOrGetRightsOverrides(): RightsEvaluationOverridesType {
        if (!this.__rightsEvaluationOverrides)
            this.__rightsEvaluationOverrides = new RightsEvaluationOverrides(this);

        return this.__rightsEvaluationOverrides;
    }

    //TODO-rites: zrevidovat, s jakými typy práv se z jakých míst volá
    protected async _newEvaluateRights(rightTypes: RightType[], priorityRightTypes?: RightType[]): Promise<void> {
        await this._rightsManager.evaluateRights(rightTypes, this.actualObject, this.isMainFrame, priorityRightTypes);
    }

    //TODO-rites: zrevidovat, s jakými typy práv se z jakých míst volá
    protected async _newEvaluateRightsGroup(rightGroup: ControlRightTypesGroup): Promise<void> {
        this._newEvaluateRights(RightGroups.getControlRightGroupTypes(rightGroup));
    }

    public async initialize(): Promise<void> {
        this._buildComponentTree();
        this._rightsManager.setComponentTree(() => this._componentTree);

        await FrameSharedMethods.runWhenFirstShown(this, () => this._initializeVisual());
    }

    /**
     * Zinicializuje celý frame. Musí být zavoláno před jeho prvním použitím. Je možné spouštět asynchronní procesy.
     */
    protected async _initializeVisual(): Promise<void> {
        if (this._initializationStarted) // inicializace může proběhnout jen jednou
            return;

        this._initializationStarted = true;

        /**
         * V této metodě by neměl být žádný výkonný kód.
         * Mělo by zde být pouze volání metod inicializujících jednotlivé oblasti a takovém pořadí, jaké je obecně potřeba
         */
        try {
            UIManager.instance.showLoader();

            // Zajistí, že oba seznamy počkají s načítáním dat až na dokončení inicialize celého TLF včetně jeho akcí
            const dataLoadingDefer = PromiseUtils.defer<boolean>();
            this._leftListFrame.deferDataLoad(dataLoadingDefer);
            this._rightListFrame.deferDataLoad(dataLoadingDefer);

            if (this.isMainFrame) {
                if (this.masterFrameName == null || this.masterLinkToAttr == null) {
                    this.masterLinkToAttr = this.ownerForm.masterLinkToAttr;
                }
            }

            if (this.masterLinkToAttr == null) {
                this._logger.warn(`MasterLinkToAttr is missing! Frame name: ${this.frameName}.`); // `
            }

            // navázání listenerů
            this._initializeListeners();

            // zpracování a zaregistrování akcí do menu
            await this._initializeActions();

            // inicializace obsažených komponent (2x ListFrame)
            await this._initializeSubComponents();

            // inicializace práv
            await this._initializeRights();

            await this._initializeConditions(dataLoadingDefer);

            this._isInitialized = true;

            this.initialized.trigger(this, undefined);
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    private async _initializeConditions(dataLoadingDefer: Deferred<boolean>): Promise<void> {
        if (this.isMainFrame) {
            if (this._listAutomaticCondition) { // Je třeba aktualizovat podmínku a omezit záznamy
                this._updateListAutomaticCondition(this.ownerForm.masterObject.id);

                await this._leftListFrame.updateCondition(this._leftListFrame.dataSource);
            }
        }

        dataLoadingDefer.resolve(true); // Teprve teď je možné začít načítat data

        if (this.masterFrame?.actualObject && !this.masterFrame.actualObject.isNew)
            this._onMasterObjectChanged();
    }

    /**
     * Inicializuje akce
     */
    private async _initializeActions(): Promise<void> {
        let action: IAction;

        if (!this.hideButtons.insert && (await OWManager.getInsertRightWithValidationRights(this.objectName)).InsertRightValue) {
            const addAction = FrameSharedMethods.createStandardRecordAction(ActionType.standardMoveRightRecord, async (e: Event, dataKey: string) => {
                EventHelper.stopPropagation(e);

                this._leftListFrame.showSaveChangesDialog(async () => {
                    if (this._leftListFrame.isEditing) { // Nepodařilo se uložit např. kvůli validační chybě
                        return;
                    }

                    await this._addItems(new OWObjectList({ Count: 1, IsForClient: false, IsSelectAll: false, Items: [{ ID: dataKey, Name: this.objectName, InstanceID: null }] }));
                });
            }, this._leftListFrame);

            this._actions.push(addAction);
            this._leftListFrame.createMenu();
            this._leftListFrame.menu.addAction(addAction);

            // Přidat
            action = FrameSharedMethods.createMultiSelectAction(ActionType.standardMoveRightRecords, async () => {
                this._prepareSelectedItems();
            }, this._leftListFrame);

            action.toMenu = MenuType.mtPrint;
            this._actions.push(action);
            this._leftListFrame.menu.addAction(action);
        }

        if (!this.hideButtons.delete && await OWManager.getDeleteRight(this.objectName)) {
            const removeAction = FrameSharedMethods.createStandardRecordAction(ActionType.standardMoveLeftRecord, async (e: Event, dataKey: string) => {
                EventHelper.stopPropagation(e);

                this._rightListFrame.showSaveChangesDialog(async () => {
                    UIManager.instance.showLoader();

                    removeAction.enabled = false;

                    try {
                        const object = await OWManager.getObject(dataKey, this.objectName);

                        if (!object.canDelete) {
                            MessageWindow.showAlert(new LocalizedStringBuilder("GUI-661277")); // Záznam nelze přesunout

                            // Trvale zakáže tlačítko u konkrétního záznamu bez ohledu na stav akce (Při úpravách nezapomenout na styly!)
                            (e.target as HTMLButtonElement).disabled = true;

                            return;
                        }

                        const result = await object.delete();

                        if (result.length > 0) {
                            UIManager.instance.showResultMessages(result);
                        }

                        await this.refreshData(false);
                    } catch (err) {
                        ErrorHandler.instance.handleError(err);
                    } finally {
                        removeAction.enabled = true;

                        UIManager.instance.hideLoader();
                    }
                });
            }, this._rightListFrame);

            this._actions.push(removeAction);
            this._rightListFrame.createMenu();
            this._rightListFrame.menu.addAction(removeAction);

            // Odebrat
            action = FrameSharedMethods.createStandardAction(ActionType.standardMoveLeftRecords, async () => {
                this._removeItems();
            }, this._rightListFrame);

            action.toMenu = MenuType.mtPrint;
            this._actions.push(action);
            this._rightListFrame.menu.addAction(action);
        }

        this.interactive = !this.ownerForm.readOnly; // Jinak se neprovede úvodní vyhodnocení akcí, pokud je TLF masterem
    }

    /**
     * Inicializuje listenery
     */
    private _initializeListeners(): void {
        this._rightListFrame.dataGridCreated.add((_, dataGrid) => {
            dataGrid.emptyMessage = new TwoListEmptyDataGridMessage(); // Vytvoříme vlastní empty message
            dataGrid.emptyMessage.emptyMessageCustomization = this._rightListFrame.emptyMessageCustomization;
        });

        this._leftListFrame.dataSaved.add(() => {
            this._rightListFrame.refreshData(false);
        });

        if (this._listAutomaticCondition) {
            this._rightListFrame.dataSaved.add(() => {
                this._leftListFrame.refreshData(false);
            });
        }

        if (this.masterFrame) {
            this._events.add(this.masterFrame.actualObjectChanged, () => {
                if (this.masterFrame.actualObject && !this.masterFrame.actualObject.isNew)
                    this._onMasterObjectChanged();
            });

            this._events.add(this.masterFrame.actualObjectSaved, () => {
                this._onMasterObjectChanged();
            });

            if (this.masterFrame instanceof ListFrame) {
                // Preview událost umožňuje zrušit cílovou událost (SelectionChanged), ale neměla by sloužit k ničemu jinému
                this._events.addListener(this.masterFrame.dataGrid, "SelectionChangedPreview", (e: IAwaitableEvent<boolean>) => {
                    this._selectionChangedPreviewHandler(e);
                });
                this._events.addListener(this.masterFrame.compactDataGrid, "SelectionChangedPreview", (e: IAwaitableEvent<boolean>) => {
                    this._selectionChangedPreviewHandler(e);
                });
            }
        }

        this.canValueLoadChanged.add(() => {
            this._newEvaluateRightsGroup(ControlRightTypesGroup.IED | ControlRightTypesGroup.initialized); // TODO-rites: ~nechat tady? asi jo; c2: spouštět až po inicializaci všech framů, jinak mohlo dojít k situaci, že se na LF iniciuje vyhodnocení práv a stromem projde na podřízený detail, který ještě není inicializován a chybí mu třeba rightsManager
        });

        this._events.add(this.ownerForm.readOnlyChanged, () => {
            this._logger.warn("- ownerform.ReadOnlyChanged");
            this._newEvaluateRights([RightType.interactive]); //TODO-rites možná nebude třeba
        });

        this._rightListFrame.selectionChanged.mergeWith(this.selectionChanged);
        this._selectionChanged = this._rightListFrame.selectionChanged;

        this._rightListFrame.actualObjectChanged.mergeWith(this.actualObjectChanged);
        this._actualObjectChanged = this._rightListFrame.actualObjectChanged;
    }

    /**
     * Prověří a případně zruší událost změny masterFramu podle stavu editace TLF
     * @param e - IAwaitableEvent
     */
    private _selectionChangedPreviewHandler(e: IAwaitableEvent<boolean>): void {
        // k možným existujícím taskům z jiných handlerů přidám další - výsledkem celého chainu může být zrušení cílové události (při false)
        e.enqueueResult(async (res) => {
            // pokud už někde dříve bylo výsledkem false, tak nemá cenu provádět další vyhodnocení
            if (res === false) {
                return false;
            }

            // Prověříme, zda některý z TLF neobsahuje změny a zobrazíme případný dialog pro jejich uložení
            if (this._leftListFrame.isChanged) {
                const result = await this._leftListFrame.showSaveChangesDialog(async () => {
                    // do nothing.
                });

                if (result === MessageButtonType.ok) {
                    await this._rightListFrame.saveRecords();
                } else if (result === MessageButtonType.no) {
                    await this._rightListFrame.cancelChanges();
                }
            } else if (this._rightListFrame.isChanged) {
                await this._rightListFrame.showSaveChangesDialog(async () => {
                    // do nothing.
                });
            } else {
                this._leftListFrame.isEditing = false;
                this._rightListFrame.isEditing = false;
            }

            return !(this._leftListFrame.isChanged || this._rightListFrame.isChanged);
        });
    }

    /**
     * Spustí postupně inicializaci obsažených ListFramů
     */
    private async _initializeSubComponents(): Promise<void> {
        if (this._leftListFrame) {
            if (this.masterFrame) { // Požadavek z testu, podle kterého se nemají v seznamu bez mastera zobrazit záznamy
                this._leftListFrame.masterFrame = this.masterFrame;
            }

            await this._leftListFrame.initialize();
        } else {
            this._logger.error(`Left ListFrame subcomponent missing! Frame name: ${this.frameName}.`); // `
        }

        if (this._rightListFrame) {
            if (this.masterFrame) {
                this._rightListFrame.masterFrame = this.masterFrame;
            }

            await this._rightListFrame.initialize();
        } else {
            this._logger.error(`Right ListFrame subcomponent missing! Frame name: ${this.frameName}.`); // `
        }

        // Popisky nad seznamy
        if (this._leftLocKey && this._leftCaptionElement) {
            Localization.addLocData(this._leftCaptionElement, this._leftLocKey);
        }

        if (this._rightLocKey && this._rightCaptionElement) {
            Localization.addLocData(this._rightCaptionElement, this._rightLocKey);
        }
    }

    /**
     * Došlo ke změně master objektu, na základě které aktualizuje podmínku levého seznamu
     */
    private async _onMasterObjectChanged(): Promise<void> {
        if (this.masterFrame.actualObject) {
            if (this._listAutomaticCondition) {
                this._updateListAutomaticCondition(this.masterFrame.actualObject.id);

                if (this.visible) {
                    await this._leftListFrame.updateConditionAndRead();
                }
            }
        }
    }

    /**
     * Nastaví na levý LF podmínku zabraňující zobrazit již obsažené záznamy z pravého LF
     * @param masterFrameObjectId - ID master objektu
     */
    private _updateListAutomaticCondition(masterFrameObjectId: any): void {
        const condition = `(NOT(exists(${this.objectName},${this.masterLinkToAttr}=${masterFrameObjectId} and ${this._framesLinkAttr}=&ID)))`; // `

        if (this._leftCondition) {
            this.leftCondition = StringUtils.combineConditions(ConditionOperator.and, condition, this._leftCondition);
        } else {
            this.leftCondition = condition;
        }
    }

    /**
     * Zruší výběr záznamů
     * @param listFrame na kterém má být provedeno zrušení výběru
     */
    private _deselectRows(listFrame: ListFrame): void {
        listFrame.clearSelectedRows();
    }

    /**
     * Naplní seznam objektů a pokusí se tyto záznamy odebrat z pravého seznamu
     */
    private async _removeItems(): Promise<void> {
        UIManager.instance.showLoader();

        const objectList = new OWObjectList(undefined);
        objectList.isSelectAll = this._rightListFrame.currentDataGrid.isSelectedAll;

        if (objectList.isSelectAll) {
            objectList.condition = this._rightListFrame.currentDataGrid.dataSource.condition;
            objectList.conditionParams = [...this._rightListFrame.currentDataGrid.dataSource.conditionParams];
            objectList.className = this._rightListFrame.objectName;
            this._rightListFrame.currentDataGrid.dataRows.filter(r => !this._rightListFrame.currentDataGrid.selectedRows.includes(r)).forEach(row => objectList.add({ id: row.dataKey, className: this.objectName }));
        } else
            this._rightListFrame.currentDataGrid.selectedRows.forEach(row => objectList.add({ id: row.dataKey, className: this.objectName }));

        const result = await OWManager.deleteObjects(this.ownerForm.formName, objectList, undefined, this._getRequestContext());

        if (result.messages.length > 0) {
            UIManager.instance.showResultMessages(result.messages);
        }

        this._deselectRows(this._rightListFrame); // Jinak po Ctrl + A zůstane nastavena dataGrid.isSelectedAll a nepůjdou přidávat nové položky do prázdného seznamu

        await this.refreshData(false);

        UIManager.instance.hideLoader();
    }

    /**
     * Naplní object list a zavolá metodu pro založení objektů v pravém seznamu
     */
    private async _prepareSelectedItems(): Promise<void> {
        const objectList = new OWObjectList(undefined);
        objectList.isSelectAll = this._leftListFrame.currentDataGrid.isSelectedAll;

        if (objectList.isSelectAll) {
            objectList.condition = this._leftListFrame.currentDataGrid.dataSource.condition;
            objectList.conditionParams = [...this._leftListFrame.currentDataGrid.dataSource.conditionParams];
            objectList.className = this._leftListFrame.objectName;
            this._leftListFrame.currentDataGrid.dataRows.filter(r => !this._leftListFrame.currentDataGrid.selectedRows.includes(r)).forEach(row => objectList.add({ id: row.dataKey, className: this.objectName }));
        } else
            this._leftListFrame.currentDataGrid.selectedRows.forEach(row => objectList.add({ id: row.dataKey, className: this.objectName }));

        await this._addItems(objectList);
    }

    /**
     * Provede vložení vybraných objektů do pravého seznamu
     * @param objects - objekty k vložení
     */
    private async _addItems(objects: OWObjectList): Promise<void> {
        UIManager.instance.showLoader();

        const afterCreateOperations = await FrameSharedMethods.createAfterCreateExtendedOperations(this._createExtendedOperationsParams(), true);
        const masterFrameObjectName = (this.masterFrame ? this.masterFrame.objectName : this.ownerForm.masterObject.objectName);
        const masterFrameObjectId = (this.masterFrame ? this.masterFrame.actualObject.id : this.ownerForm.masterObject.id);

        try {
            const result = await OWManager.createObjectsFromList(this.objectName, masterFrameObjectName, this.masterLinkToAttr,
                masterFrameObjectId, objects, this._framesLinkAttr, afterCreateOperations, this._getRequestContext());

            if (result.messages.length > 0) {
                UIManager.instance.showResultMessages(result.messages);
            }

            if (result.objects.length > 0) {
                if (this._listAutomaticCondition) {
                    await this.refreshData(false);
                } else {
                    await this._rightListFrame.refreshData(false);
                    await this._rightListFrame.refreshGroupTree(true);

                    FrameSharedMethods.refreshFrameAfterSetActive(this.ownerForm);
                }
            }
        } catch (err) {
            ErrorHandler.instance.handleError(err);
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    private _createExtendedOperationsParams(): IExtendedOperationsParams {
        return {
            ownerForm: this.ownerForm,
            masterFrame: this.masterFrame,
            afterGuiCreateMethod: this._afterGuiCreateMethod,
            afterGuiCreateFill: this._afterGuiCreateFill,
            masterLinkToAttr: this.masterLinkToAttr,
            masterAttr: this.masterAttr,
            isMainFrame: this.isMainFrame,
        };
    }

    protected _getRequestContext(cancellationToken: CancellationToken = CancellationToken.none): RequestContext {
        return convertContextFromFrameToRequest(this.frameContext, cancellationToken);
    }

    public override dispose(): void {
        super.dispose();
        this._leftListFrame?.dispose();
        this._rightListFrame?.dispose();
    }
    //#endregion Methods
}

/**
 * Levý seznam komponenty
 */
class LeftListFrame extends ListFrame {
    constructor(params: FormControlParams, rightListFrame: RightListFrame) {
        super(params);
        this._rightListFrame = rightListFrame;
    }

    /**
    * Zjištění, zda by se měl posílat dotaz na vyhodnocení InsertRight
    */
    public override get hasInsertControl(): boolean {
        return true;
    }

    private readonly _rightListFrame: RightListFrame;

    public override get canInsert(): boolean | undefined | null {
        return this._rightListFrame?.canInsert;
    }

    public override set canInsert(value: boolean) {
        super.canInsert = value;
    }

    /**
     * Vytvoří inicializační podmínku pro datasource seznamu
     */
    public override async createReaderCondition(): Promise<string> {
        // v tomto případě nechceme použít omezení z předka this.ownerForm.masterLinkToAttr + "=" + this.ownerForm.masterObject.id protože by to nenalezlo daný atribut
        if (this.isMainFrame) {
            if (!this.formCanRead/*this.frame.canRead*/) // pokud je na formuláři tvrdým právem zakázano čtení dat, tak je to řešeno nesplnitelnou podmínkou - sice může docházet ke zbytečným voláním služby, ale bude díky tomu fungovat (i refreshe a pod.)
                return "1=0";

            return FrameSharedMethods.appendReadRightCondition(this.condition, this);
        }

        return super.createReaderCondition();
    }

    protected override __rightsEvaluationOverrides: TwoListFrameRightsEvaluationOverrides;

    protected override _createOrGetRightsOverrides(): TwoListFrameRightsEvaluationOverrides {
        if (!this.__rightsEvaluationOverrides)
            this.__rightsEvaluationOverrides = new TwoListFrameRightsEvaluationOverrides(this);

        return this.__rightsEvaluationOverrides;
    }
}

/**
 * Pravý seznam komponenty
 */
class RightListFrame extends ListFrame {
    constructor(params: FormControlParams) {
        super(params);
    }

    /**
    * Zjištění, zda by se měl posílat dotaz na vyhodnocení InsertRight
    */
    public override get hasInsertControl(): boolean {
        return true;
    }

    protected override __rightsEvaluationOverrides: TwoListFrameRightsEvaluationOverrides;

    protected override _createOrGetRightsOverrides(): TwoListFrameRightsEvaluationOverrides {
        if (!this.__rightsEvaluationOverrides)
            this.__rightsEvaluationOverrides = new TwoListFrameRightsEvaluationOverrides(this);

        return this.__rightsEvaluationOverrides;
    }
}

type PartialFrameParams = {
    isEditable: boolean;
    reportAttribute: string;
    exportAttribute: string;
    defaultGroup: string;
    groupWidth: string;
    showOnMainForm: string;
    showFilterValues: string;
    ownerForm: IForm;
};
